import axios, { AxiosError, AxiosResponse } from 'axios';

interface LoginAPIResponse {
  email: string;
  reason: string;
  success: string;
}

interface ForgotPasswordAPIResponse {
  reason: string;
  success: string;
}

interface Generate2FAAPIResponse {
  success: string;
}

interface LoginResponse {
  email?: string;
  reason?: string;
  status?: number;
  success?: string;
}

interface Generate2FAResponse {
  success?: string;
}

interface ForgotPasswordResponse {
  reason?: string;
  status?: number;
  success?: string;
}

function setupBaseHeaders(envId: string) {
  const baseHeaders: Record<string, string> = {};

  if (envId === 'production') return baseHeaders;

  baseHeaders['X-HTTP-Request-Source'] = `React-${envId}`;

  return baseHeaders;
}

export function setupAuthentication(jwt: string) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const configureHeaders = (config: Record<string, any>) => {
    config.headers = {
      ...config.headers,
      Authorization: `JWT ${jwt}`,
    };

    return config;
  };

  djangoApi.interceptors.request.use(configureHeaders);
}

export const djangoApi = axios.create({
  baseURL: CONFIG.djangoBaseUrl,
  headers: setupBaseHeaders(ENV_ID),
  withCredentials: true,
});

function login(
  email: string,
  password: string,
  gRecaptchaResponse: string | null,
  challengeCode?: string,
): Promise<LoginResponse> {
  return djangoApi
    .request({
      url: '/api/auth/login/',
      method: 'POST',
      data: { email, password, gRecaptchaResponse, challengeCode },
    })
    .then((response: AxiosResponse<LoginAPIResponse>) => {
      const {
        data: { reason, success },
      } = response;
      return { reason, success };
    })
    .catch((error: AxiosError) => {
      const response: AxiosResponse<LoginAPIResponse> | undefined = error.response;
      if (!response) {
        return {};
      }
      const {
        status,
        data: { reason, success },
      } = response;
      return { reason, status, success };
    });
}

export enum LoginFailures {
  INVALID_CREDS = 'invalid_credentials',
  INVALID_CAPTCHA = 'invalid_captcha',
  EXCESSIVE_LOGINS = 'login_max_attempts_exceeded',
  EXPIRED_CODE = '2fa_code_expired',
  INVALID_CODE = '2fa_invalid_code',
  EXCESSIVE_2FA = '2fa_max_attempts_exceeded',
  CHALLENGE_2FA = 'requires_2fa',
  LOGIN_FAILED = 'login_failed',
  SSO_AUTHENTICATION_FAILED = 'unauthenticated',
}

export enum TwoFactorMessages {
  RESEND_2FA_SUCCESS = 'resend_2FA_success',
}

function forgotPassword(
  email: string,
  gRecaptchaResponse: string | null,
): Promise<ForgotPasswordResponse> {
  return djangoApi
    .request({
      url: '/api/auth/forgotpassword/',
      method: 'POST',
      data: { email, gRecaptchaResponse },
    })
    .then((response: AxiosResponse<ForgotPasswordAPIResponse>) => {
      const {
        status,
        data: { reason, success },
      } = response;
      return { reason, status, success };
    })
    .catch((error: AxiosError) => {
      const response: AxiosResponse<ForgotPasswordAPIResponse> | undefined = error.response;
      if (!response) {
        return {};
      }
      const {
        status,
        data: { reason, success },
      } = response;
      return { reason, status, success };
    });
}

function generate2FA(email: string): Promise<Generate2FAResponse> {
  return djangoApi
    .request({
      url: '/api/auth/generate2fa/',
      method: 'POST',
      data: { email },
    })
    .then((response: AxiosResponse<Generate2FAAPIResponse>) => {
      const {
        data: { success },
      } = response;
      return { success };
    })
    .catch((error: AxiosError) => {
      const response: AxiosResponse<Generate2FAAPIResponse> | undefined = error.response;
      if (!response) {
        return {};
      }
      const {
        data: { success },
      } = response;
      return { success };
    });
}

export { login, forgotPassword, generate2FA };
